import { Button, Chip, CircularProgress, Divider, MenuItem, TextField } from '@mui/material';
import { Building2, Plus, RefreshCw, Users } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import {
  createIntegrationClient,
  listAgents,
  listIntegrationClientBindings,
  listIntegrationClients,
  provisionIntegrationClientAgent,
  updateIntegrationClientBinding,
  upsertIntegrationClientBinding,
} from '../../services/api';
import {
  deleteIntegrationClient,
  updateIntegrationClient,
} from '../../services/integration-client.service';
import type { Agent, ExternalAgentBinding, IntegrationClient } from '../../types';
import { useNotification } from '../../hooks/useNotification';
import { useTranslation } from 'react-i18next';
import { BindingsTable } from './BindingsTable';
import {
  BindExistingAgentForm,
  ProvisionAgentForm,
  type BindingFormState,
  type ProvisionFormState,
} from './ClientBindingForms';
import { INTEGRATION_CLIENT_ENVIRONMENTS } from './constants';
import { IntegrationClientHeader } from './IntegrationClientHeader';
import { IntegrationClientList } from './IntegrationClientList';
import {
  DeleteIntegrationClientDialog,
  EditIntegrationClientDialog,
} from './IntegrationClientDialogs';

export const IntegrationClientsPanel: React.FC = () => {
  const { t } = useTranslation();
  const { success, error } = useNotification();
  const [clients, setClients] = useState<IntegrationClient[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [bindings, setBindings] = useState<ExternalAgentBinding[]>([]);
  const [selectedClientPublicId, setSelectedClientPublicId] = useState('');
  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingBindings, setLoadingBindings] = useState(false);
  const [savingClient, setSavingClient] = useState(false);
  const [savingBinding, setSavingBinding] = useState(false);
  const [provisioningAgent, setProvisioningAgent] = useState(false);
  const [editingClient, setEditingClient] = useState<IntegrationClient | null>(null);
  const [deletingClient, setDeletingClient] = useState<IntegrationClient | null>(null);
  const [clientActionBusy, setClientActionBusy] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [newClient, setNewClient] = useState({ name: '', environment: 'live' });
  const [bindingForm, setBindingForm] = useState<BindingFormState>({
    agentPublicId: '',
    externalTenantId: '',
    externalTenantType: 'salon',
  });
  const [provisionForm, setProvisionForm] = useState<ProvisionFormState>({
    externalTenantId: '',
    externalTenantType: 'salon',
    templateAgentPublicId: '',
    name: '',
  });

  const selectedClient = clients.find((client) => client.public_id === selectedClientPublicId);
  const filteredClients = useMemo(() => {
    const normalizedSearch = clientSearchTerm.trim().toLowerCase();
    if (!normalizedSearch) return clients;

    return clients.filter((client) =>
      [
        client.name,
        client.public_id,
        client.environment,
        client.status,
        String(client.binding_count ?? 0),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [clientSearchTerm, clients]);

  const loadClients = async () => {
    setLoadingClients(true);
    const [clientsRes, agentsRes] = await Promise.all([listIntegrationClients(), listAgents()]);

    if (clientsRes.success && clientsRes.data) {
      setClients(clientsRes.data);
      setSelectedClientPublicId((current) => current || clientsRes.data?.[0]?.public_id || '');
    } else {
      error(clientsRes.error || t('apiKeys.errors.loadIntegrationClientsFailed'));
    }

    if (agentsRes.success && agentsRes.data) {
      setAgents(agentsRes.data);
    } else {
      error(agentsRes.error || t('apiKeys.errors.loadAgentsFailed'));
    }

    setLoadingClients(false);
  };

  const loadBindings = async (clientPublicId: string) => {
    if (!clientPublicId) {
      setBindings([]);
      return;
    }

    setLoadingBindings(true);
    const response = await listIntegrationClientBindings(clientPublicId);

    if (response.success && response.data) {
      setBindings(response.data);
    } else {
      error(response.error || t('apiKeys.errors.loadBindingsFailed'));
    }

    setLoadingBindings(false);
  };

  useEffect(() => {
    void loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadBindings(selectedClientPublicId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientPublicId]);

  const handleCreateClient = async () => {
    const name = newClient.name.trim();
    if (!name || savingClient) return;

    setSavingClient(true);
    const response = await createIntegrationClient({
      name,
      environment: newClient.environment,
      status: 'active',
    });

    if (response.success && response.data) {
      setClients((prev) => [response.data as IntegrationClient, ...prev]);
      setSelectedClientPublicId(response.data.public_id);
      setClientSearchTerm('');
      setNewClient({ name: '', environment: 'live' });
      success(t('apiKeys.messages.integrationClientCreated'));
    } else {
      error(response.error || t('apiKeys.errors.createIntegrationClientFailed'));
    }

    setSavingClient(false);
  };

  const handleCreateBinding = async () => {
    if (!selectedClientPublicId || !bindingForm.agentPublicId || !bindingForm.externalTenantId) {
      return;
    }

    setSavingBinding(true);
    const response = await upsertIntegrationClientBinding(selectedClientPublicId, {
      agent_public_id: bindingForm.agentPublicId,
      external_tenant_id: bindingForm.externalTenantId.trim(),
      external_tenant_type: bindingForm.externalTenantType.trim() || undefined,
      status: 'active',
    });

    if (response.success) {
      success(t('apiKeys.messages.bindingSaved'));
      setBindingForm({ agentPublicId: '', externalTenantId: '', externalTenantType: 'salon' });
      await loadBindings(selectedClientPublicId);
      await loadClients();
    } else {
      error(response.error || t('apiKeys.errors.saveBindingFailed'));
    }

    setSavingBinding(false);
  };

  const handleProvisionAgent = async () => {
    if (!selectedClientPublicId || !provisionForm.externalTenantId) return;

    setProvisioningAgent(true);
    const response = await provisionIntegrationClientAgent(selectedClientPublicId, {
      external_tenant_id: provisionForm.externalTenantId.trim(),
      external_tenant_type: provisionForm.externalTenantType.trim() || undefined,
      template_agent_public_id: provisionForm.templateAgentPublicId || undefined,
      name: provisionForm.name.trim() || undefined,
      status: 'active',
    });

    if (response.success) {
      success(t('apiKeys.messages.tenantAgentProvisioned'));
      setProvisionForm({
        externalTenantId: '',
        externalTenantType: 'salon',
        templateAgentPublicId: '',
        name: '',
      });
      await loadBindings(selectedClientPublicId);
      await loadClients();
    } else {
      error(response.error || t('apiKeys.errors.provisionAgentFailed'));
    }

    setProvisioningAgent(false);
  };

  const handleBindingStatus = async (binding: ExternalAgentBinding, status: string) => {
    if (!selectedClientPublicId) return;

    const response = await updateIntegrationClientBinding(
      selectedClientPublicId,
      binding.public_id,
      {
        status,
      }
    );

    if (response.success) {
      success(t('apiKeys.messages.bindingUpdated'));
      await loadBindings(selectedClientPublicId);
    } else {
      error(response.error || t('apiKeys.errors.updateBindingFailed'));
    }
  };

  const replaceClient = (updated: IntegrationClient) =>
    setClients((prev) =>
      prev.map((client) => (client.public_id === updated.public_id ? updated : client))
    );

  const handleUpdateClient = async (
    client: IntegrationClient,
    input: { name?: string; environment?: string; status?: string }
  ): Promise<boolean> => {
    setClientActionBusy(true);
    const response = await updateIntegrationClient(client.public_id, input);
    setClientActionBusy(false);

    if (response.success && response.data) {
      replaceClient({ ...client, ...response.data });
      success(t('apiKeys.messages.integrationClientUpdated'));
      return true;
    }

    error(response.error || t('apiKeys.errors.updateIntegrationClientFailed'));
    return false;
  };

  const handleSaveClientEdit = async (input: { name: string; environment: string }) => {
    if (!editingClient) return;
    if (await handleUpdateClient(editingClient, input)) setEditingClient(null);
  };

  const handleToggleClientStatus = (client: IntegrationClient) =>
    handleUpdateClient(client, { status: client.status === 'active' ? 'disabled' : 'active' });

  const handleDeleteClient = async () => {
    if (!deletingClient) return;

    setClientActionBusy(true);
    const response = await deleteIntegrationClient(deletingClient.public_id);
    setClientActionBusy(false);

    if (!response.success) {
      error(response.error || t('apiKeys.errors.deleteIntegrationClientFailed'));
      return;
    }

    const remaining = clients.filter((client) => client.public_id !== deletingClient.public_id);
    setClients(remaining);
    setSelectedClientPublicId((current) =>
      current === deletingClient.public_id ? (remaining[0]?.public_id ?? '') : current
    );
    setDeletingClient(null);
    success(t('apiKeys.messages.integrationClientDeleted'));
  };

  const totalBindings = clients.reduce((sum, client) => sum + (client.binding_count ?? 0), 0);

  return (
    <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
              {t('apiKeys.integrationClients.title')}
            </h2>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
            {t('apiKeys.integrationClients.subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Chip
            size="small"
            icon={<Building2 size={14} />}
            label={t('apiKeys.integrationClients.clientCount', { count: clients.length })}
            variant="outlined"
          />
          <Chip
            size="small"
            icon={<Users size={14} />}
            label={t('apiKeys.integrationClients.bindingCount', { count: totalBindings })}
            variant="outlined"
          />
          <Button
            type="button"
            size="small"
            variant="outlined"
            startIcon={<RefreshCw size={16} />}
            onClick={() => void loadClients()}
            disabled={loadingClients}
          >
            {t('apiKeys.integrationClients.refresh')}
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_160px_auto]">
        <TextField
          fullWidth
          size="small"
          label={t('apiKeys.integrationClients.clientName')}
          placeholder="NailMap Production"
          value={newClient.name}
          onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
        />
        <TextField
          select
          fullWidth
          size="small"
          label={t('apiKeys.modal.environment')}
          value={newClient.environment}
          onChange={(e) => setNewClient({ ...newClient, environment: e.target.value })}
        >
          {INTEGRATION_CLIENT_ENVIRONMENTS.map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
        <Button
          type="button"
          variant="contained"
          startIcon={
            savingClient ? <CircularProgress size={16} color="inherit" /> : <Plus size={16} />
          }
          disabled={!newClient.name.trim() || savingClient}
          onClick={handleCreateClient}
        >
          {t('apiKeys.integrationClients.createClient')}
        </Button>
      </div>

      <Divider className="!my-4" />

      {loadingClients ? (
        <div className="flex items-center gap-2 py-6 text-sm text-gray-500 dark:text-slate-400">
          <CircularProgress size={18} />
          {t('apiKeys.integrationClients.loading')}
        </div>
      ) : clients.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-6 text-sm text-gray-500 dark:border-slate-600 dark:text-slate-400">
          {t('apiKeys.integrationClients.empty')}
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
          <IntegrationClientList
            clients={filteredClients}
            selectedPublicId={selectedClientPublicId}
            searchTerm={clientSearchTerm}
            onSearchChange={setClientSearchTerm}
            onSelect={setSelectedClientPublicId}
          />

          <div className="min-w-0">
            {selectedClient && (
              <IntegrationClientHeader
                client={selectedClient}
                hasActiveBindings={bindings.some((binding) => binding.status === 'active')}
                busy={clientActionBusy}
                onEdit={() => setEditingClient(selectedClient)}
                onToggleStatus={() => void handleToggleClientStatus(selectedClient)}
                onDelete={() => setDeletingClient(selectedClient)}
              />
            )}

            <div className="grid gap-3 xl:grid-cols-2">
              <BindExistingAgentForm
                agents={agents}
                form={bindingForm}
                setForm={setBindingForm}
                saving={savingBinding}
                clientSelected={Boolean(selectedClientPublicId)}
                onSubmit={handleCreateBinding}
              />
              <ProvisionAgentForm
                agents={agents}
                form={provisionForm}
                setForm={setProvisionForm}
                saving={provisioningAgent}
                clientSelected={Boolean(selectedClientPublicId)}
                onSubmit={handleProvisionAgent}
              />
            </div>

            <div className="mt-4">
              <BindingsTable
                bindings={bindings}
                loading={loadingBindings}
                canRefresh={Boolean(selectedClientPublicId)}
                onRefresh={() => void loadBindings(selectedClientPublicId)}
                onStatusChange={(binding, status) => void handleBindingStatus(binding, status)}
              />
            </div>
          </div>
        </div>
      )}

      <EditIntegrationClientDialog
        client={editingClient}
        saving={clientActionBusy}
        onClose={() => setEditingClient(null)}
        onSave={(input) => void handleSaveClientEdit(input)}
      />
      <DeleteIntegrationClientDialog
        client={deletingClient}
        deleting={clientActionBusy}
        onClose={() => setDeletingClient(null)}
        onConfirm={() => void handleDeleteClient()}
      />
    </section>
  );
};
